/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    USER_DELETE_FEATURE
 * Date:         2025-01-13
 * Modified By:  Cursor
 *
 * Description:
 * Enhanced ConfirmDialog component with accessibility features including ARIA attributes,
 * focus management, and keyboard navigation support for delete user confirmation.
 *
 * Related Screens / Modules:
 * - client/src/features/admin/users/UserEditModal.tsx
 * - client/src/features/admin/users/ConfirmDialog.tsx
 */
import React, { useEffect, useRef } from "react";
import { Modal, Spinner } from "react-bootstrap";
import AdminButton from "../../../components/admin/AdminButton";
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ADMIN_BUTTON_USAGE_CONFIRM_DIALOG
 * Date:         2026-01-07
 * Modified By:  Cursor
 *
 * Description:
 * Replace react-bootstrap Buttons with AdminButton and map variant props to
 * intent tokens; preserve accessibility by forwarding refs to the underlying button.
 *
 * File Size Check:
 * - Current: 168
 * - Adding: ~18
 * - Projected: ~186
 * - Decision: Proceed
 *
 * Typography Compliance (UI files only):
 * - Utilities used: .u-P2-16-medium (via btn-figma-1 / btn-danger-figma)
 * - Tokens used: --font-size-body-small, --line-height-body-small
 *
 * Related Files:
 * - client/src/components/admin/AdminButton.tsx
 * - client/src/features/admin/users/ConfirmDialog.tsx
 */

interface ConfirmDialogProps {
  show: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?:
    | "primary"
    | "secondary"
    | "success"
    | "danger"
    | "warning"
    | "info"
    | "light"
    | "dark";
  cancelVariant?:
    | "primary"
    | "secondary"
    | "success"
    | "danger"
    | "warning"
    | "info"
    | "light"
    | "dark"
    | "outline-primary"
    | "outline-secondary"
    | "outline-success"
    | "outline-danger"
    | "outline-warning"
    | "outline-info"
    | "outline-light"
    | "outline-dark";
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  size?: "sm" | "lg" | "xl";
  variant?: "danger" | "warning" | "info";
}

export default function ConfirmDialog({
  show,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmVariant = "primary",
  cancelVariant = "outline-secondary",
  onConfirm,
  onCancel,
  isLoading = false,
  size = "sm",
  variant,
}: ConfirmDialogProps) {
  // [2025-01-13] accessibility: ref for focus management on confirm button
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  // [2025-01-13] accessibility: ref for returning focus to trigger button
  const triggerButtonRef = useRef<HTMLButtonElement | null>(null);

  // [2025-01-13] accessibility: focus confirm button when dialog opens
  useEffect(() => {
    if (show && confirmButtonRef.current && !isLoading) {
      // Store the currently focused element before opening dialog
      triggerButtonRef.current = document.activeElement as HTMLButtonElement;
      // Small delay to ensure modal is fully rendered
      setTimeout(() => {
        confirmButtonRef.current?.focus();
      }, 100);
    }
  }, [show, isLoading]);

  // [2025-01-13] accessibility: handle ESC key to cancel
  useEffect(() => {
    if (!show) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isLoading) {
        onCancel();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [show, isLoading, onCancel]);

  // [2025-01-13] accessibility: return focus to trigger button when dialog closes
  useEffect(() => {
    if (!show && triggerButtonRef.current) {
      triggerButtonRef.current.focus();
      triggerButtonRef.current = null;
    }
  }, [show]);

  // [2025-01-13] ui: determine variant-based styling
  // [2025-01-13] status-change: handle warning variant for status change confirmations
  const finalConfirmVariant =
    variant === "danger" ? "danger" : variant === "warning" ? "warning" : confirmVariant;
  const finalCancelVariant = cancelVariant;

  const mapVariantToIntent = (v?: string) => {
    if (!v) return "secondary";
    const lower = v.toLowerCase();
    if (lower.includes("danger")) return "danger";
    if (lower.startsWith("outline-")) return "secondary";
    if (lower === "primary") return "primary";
    if (lower === "link") return "link";
    return "secondary";
  };

  // [2025-01-13] accessibility: generate unique IDs for ARIA attributes
  const titleId = `confirm-dialog-title-${title.replace(/\s+/g, "-").toLowerCase()}`;
  const messageId = `confirm-dialog-message-${title.replace(/\s+/g, "-").toLowerCase()}`;

  return (
    <Modal
      show={show}
      onHide={onCancel}
      size={size}
      centered
      // [2025-01-13] accessibility: ARIA attributes for alert dialog
      role="alertdialog"
      aria-labelledby={titleId}
      aria-describedby={messageId}
      aria-modal="true"
    >
      <Modal.Header closeButton>
        <Modal.Title id={titleId}>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p id={messageId} className="mb-0" style={{ whiteSpace: "pre-line" }}>
          {message}
        </p>
      </Modal.Body>
        <Modal.Footer>
        <AdminButton
          intent={mapVariantToIntent(finalCancelVariant) as any}
          onClick={onCancel}
          disabled={isLoading}
        >
          {cancelText}
        </AdminButton>
        <AdminButton
          ref={confirmButtonRef}
          intent={mapVariantToIntent(finalConfirmVariant) as any}
          onClick={onConfirm}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Processing...
            </>
          ) : (
            confirmText
          )}
        </AdminButton>
      </Modal.Footer>
    </Modal>
  );
}
